import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { find } from 'lodash'
import _ from 'lodash'
import { connect } from 'react-redux'
import Selectors from '../../../../selectors'
import { formatFieldWithKeys } from '../../../../selectors/RiskAssessments'
import Operations from '../../../../operations'
import { groupDependentFields } from '../../../../common/utils'
import { colors, sizes } from '@lmc/common/constants'
import { Spinner, IconSectionHeader } from '../../../../components/generic'
import { LastUpdateBox } from '../../../../components/carePlans'
import FormFields from '../../../../components/forms/FormFields'
import RiskAssessmentOutcome from '../../../../components/forms/screens/RiskAssessmentForm/RiskAssessmentOutcome'
import RiskAssessmentPdfExport from '../../../../components/pdf/RiskAssessmentPdfExport'
import { isBrowser, isTablet } from 'react-device-detect'
import { Button, Text } from '@lmc/ui-kit'
import Glyph from '../../../../LEGACY/Glyph'
import Actions from '../../../../actions'
import { withRouter } from '../../../../withRouter'
import { history } from '../../../../store'
import CarePlansPopup from '../../../../components/carePlans/CarePlansPopup'
import dayjs from 'dayjs'
import SeenBySection from './components/SeenBySection'
import RiskAssessmentViewFooter from './components/RiskAssessmentViewFooter'
import BackNavigationHeader from './components/BackNavigationHeader'
import useRiskAssessmentData from '../../../../components/modals/CreateRiskAssessment/hooks/useRiskAssessmentData'

const isDesktop = isBrowser || isTablet

export const ResidentRiskAssessmentScreen = (props) => {
  const {
    carers,
    resident,
    carePlanNominative,
    hasAccess,
    openRiskAssessmentModal,
    loadListItem,
    testId,
    router: {
      params: { id, risk_assessment_id, risk_assessment_history_id },
    },
  } = props

  const chosenId = risk_assessment_history_id || risk_assessment_id

  const {
    riskAssessment,
    isLoading: isLoadingRiskAssessment,
    error: riskAssessmentError,
  } = useRiskAssessmentData(chosenId)

  useEffect(() => {
    if (riskAssessment) {
      loadListItem('risk-assessments', riskAssessment.id)
      if (riskAssessment.form?.id) {
        loadListItem('forms', riskAssessment.form.id)
      }
    }
  }, [riskAssessment, loadListItem])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const shouldEdit = urlParams.get('edit') === 'true'

    if (shouldEdit && riskAssessment && !isLoadingRiskAssessment) {
      openRiskAssessmentModal(riskAssessment)
      const newUrl = window.location.pathname + window.location.hash
      window.history.replaceState({}, '', newUrl)
    }
  }, [riskAssessment, isLoadingRiskAssessment, openRiskAssessmentModal])

  const formatFormForAssessment = (rawForm) => {
    if (!rawForm) return null

    const withKeys = _.map(rawForm.formFields, formatFieldWithKeys)
    const formFields = groupDependentFields(withKeys)

    return { ...rawForm, formFields }
  }

  const formatFormForAssessmentDisplay = (rawForm) => {
    if (!rawForm) return null

    const withKeys = _.map(rawForm.formFields, formatFieldWithKeys)
    const formFields = groupDependentFields(withKeys).map((field) => {
      if (field.dependentFields) {
        return {
          ...field,
          dependentFields: field.dependentFields.filter(
            (entry) => entry.type !== 'suggestedActions',
          ),
        }
      }
      return { ...field }
    })

    return { ...rawForm, formFields }
  }

  const form = formatFormForAssessment(riskAssessment?.form)
  const displayForm = formatFormForAssessmentDisplay(riskAssessment?.form)

  const goBack = () => history.back()

  const onClickHistory = () => {
    history.push(
      `/residents/${id}/risk-assessments/${risk_assessment_id}/history`,
    )
  }

  const getCarer = () => {
    return find(carers, { id: riskAssessment?.createdBy })
  }

  const renderFields = () => {
    return (
      <div
        style={styles.sectionContainer}
        data-testid={
          testId
            ? `${testId}-risk-assessment-fields-section`
            : 'risk-assessment-fields-section'
        }
      >
        <FormFields
          errors={{}}
          form={displayForm}
          fields={riskAssessment.formData}
          readOnly
          userGenerated
          data-testid={
            testId
              ? `${testId}-risk-assessment-form-fields`
              : 'risk-assessment-form-fields'
          }
        />
      </div>
    )
  }

  const renderCreatedBy = () => {
    const { createdBy, createdOn, reviewedOn } = riskAssessment
    const carer = getCarer()

    if (createdOn && createdBy && !carer) return null

    const showRiskAssessmentCarePlansPopup = !!riskAssessment?.carePlans?.length

    return (
      <LastUpdateBox
        carer={carer}
        date={reviewedOn || createdOn}
        style={styles.lastUpdateBox}
        data-testid={
          testId
            ? `${testId}-risk-assessment-last-update-box`
            : 'risk-assessment-last-update-box'
        }
        rightChildren={
          <div
            style={{
              marginLeft: 'auto',
              marginRight: 5,
              display: 'flex',
              alignItems: 'center',
            }}
            data-testid={
              testId
                ? `${testId}-risk-assessment-attachments-container`
                : 'risk-assessment-attachments-container'
            }
          >
            {showRiskAssessmentCarePlansPopup ? (
              <>
                <Text as="p2">Attachments:</Text>
                <CarePlansPopup
                  riskAssessmentCarePlans={riskAssessment?.carePlans}
                  carePlanNominative={carePlanNominative}
                  data-testid={
                    testId
                      ? `${testId}-risk-assessment-care-plans-popup`
                      : 'risk-assessment-care-plans-popup'
                  }
                />
              </>
            ) : null}
          </div>
        }
      />
    )
  }

  const renderHeader = () => {
    const pdfTitle = `${resident.name.first} ${resident.name.last} - ${riskAssessment.form.name}`
    return (
      <div
        data-testid={
          testId ? `${testId}-risk-assessment-header` : 'risk-assessment-header'
        }
      >
        <BackNavigationHeader
          onBack={goBack}
          archiveMetadata={riskAssessment.archiveMetadata}
          testId={testId}
        />
        <div
          style={{
            ...styles.titleContainer,
            marginLeft: isDesktop && !riskAssessment.form.iconText ? 55 : 0,
          }}
          data-testid={
            testId
              ? `${testId}-risk-assessment-title-container`
              : 'risk-assessment-title-container'
          }
        >
          <IconSectionHeader
            isMainHeader
            title={riskAssessment.form.name}
            iconText={riskAssessment.form.iconText}
            color={riskAssessment.form.color}
            data-testid={
              testId
                ? `${testId}-risk-assessment-icon-header`
                : 'risk-assessment-icon-header'
            }
          />
          <div
            style={styles.containerButton}
            data-testid={
              testId
                ? `${testId}-risk-assessment-header-buttons`
                : 'risk-assessment-header-buttons'
            }
          >
            {!risk_assessment_history_id && (
              <Button
                id="risk-assessment-history"
                onClick={onClickHistory}
                style={styles.historyButton}
                variant="default"
                icon={<Glyph name="book" />}
                data-testid={
                  testId
                    ? `${testId}-risk-assessment-history-button`
                    : 'risk-assessment-history-button'
                }
              >
                History
              </Button>
            )}
            {isDesktop && (
              <RiskAssessmentPdfExport
                carer={getCarer()}
                id="risk-assessment-pdf-download-button"
                resident={resident}
                riskAssessment={riskAssessment}
                title={pdfTitle}
                archiveMetadata={riskAssessment.archiveMetadata}
                data-testid={
                  testId
                    ? `${testId}-risk-assessment-pdf-export`
                    : 'risk-assessment-pdf-export'
                }
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderOutcomes = () => {
    const { riskLevel, riskScore } = riskAssessment

    if (form && !form.hasRiskScore) return null

    return (
      <div
        style={styles.sectionContainer}
        data-testid={
          testId
            ? `${testId}-risk-assessment-outcomes-section`
            : 'risk-assessment-outcomes-section'
        }
      >
        <Text
          color="black"
          style={{ opacity: 0.7 }}
          as="h4"
          data-testid={
            testId
              ? `${testId}-risk-assessment-outcomes-title`
              : 'risk-assessment-outcomes-title'
          }
        >
          Risk Outcome Score
        </Text>
        <div
          style={styles.fieldContainer}
          data-testid={
            testId
              ? `${testId}-risk-assessment-outcome-container`
              : 'risk-assessment-outcome-container'
          }
        >
          <RiskAssessmentOutcome
            hasActions={form.hasActions}
            resident={resident}
            risk={{
              level: riskLevel,
              score: riskScore,
            }}
            data-testid={
              testId
                ? `${testId}-risk-assessment-outcome`
                : 'risk-assessment-outcome'
            }
          />
        </div>
      </div>
    )
  }

  const renderActions = () => {
    if (form && !form.hasActions) return null
    return (
      <div
        style={styles.sectionContainer}
        data-testid={
          testId
            ? `${testId}-risk-assessment-actions-section`
            : 'risk-assessment-actions-section'
        }
      >
        <Text
          color="black"
          style={{ opacity: 0.7 }}
          as="h4"
          data-testid={
            testId
              ? `${testId}-risk-assessment-actions-title`
              : 'risk-assessment-actions-title'
          }
        >
          Actions to mitigate this risk
        </Text>
        {riskAssessment.formData.actions && (
          <div
            className="lmc-card"
            style={{
              ...styles.fieldContainer,
              ...styles.cardContainer,
            }}
            data-testid={
              testId
                ? `${testId}-risk-assessment-actions-card`
                : 'risk-assessment-actions-card'
            }
          >
            {riskAssessment.formData.actions}
          </div>
        )}
      </div>
    )
  }

  const renderSeenBy = () => {
    const avatars = riskAssessment.seenBy.map((user) => {
      const currentUser = carers.find((carer) => carer.id === user.userId)
      if (!currentUser) return null
      return {
        url: currentUser.picture,
        name: `${currentUser.name.first} ${currentUser.name.last}`,
        viewedOn: `Viewed on: ${dayjs(user.timestamp).format('DD/MM/YYYY')}`,
      }
    })
    const filteredAvatars = avatars.filter(Boolean)
    return (
      <SeenBySection
        filteredAvatars={filteredAvatars}
        data-testid={
          testId
            ? `${testId}-risk-assessment-seen-by-section`
            : 'risk-assessment-seen-by-section'
        }
      />
    )
  }

  const renderFooter = () => {
    const hasRolePermission = hasAccess(
      ['carehome-admin', 'senior-carer'],
      [
        {
          i: 'update',
          this: 'RiskAssessment',
        },
      ],
    )

    if (risk_assessment_history_id) return
    return (
      <RiskAssessmentViewFooter
        residentId={id}
        onEdit={() => openRiskAssessmentModal(riskAssessment)}
        hasEditPermission={hasRolePermission}
        isHistoryView={!!risk_assessment_history_id}
        archived={riskAssessment.archived}
        riskAssessmentId={riskAssessment.id}
        testId={testId}
      />
    )
  }

  if (isLoadingRiskAssessment)
    return (
      <Spinner
        data-testid={
          testId
            ? `${testId}-risk-assessment-screen-loading`
            : 'risk-assessment-screen-loading'
        }
      />
    )

  if (riskAssessmentError) {
    console.error('Error loading risk assessment:', riskAssessmentError)
    return (
      <div
        data-testid={
          testId
            ? `${testId}-risk-assessment-screen-error`
            : 'risk-assessment-screen-error'
        }
      >
        Error loading risk assessment
      </div>
    )
  }

  if (!riskAssessment || !riskAssessment.formData)
    return (
      <Spinner
        data-testid={
          testId
            ? `${testId}-risk-assessment-screen-no-data-loading`
            : 'risk-assessment-screen-no-data-loading'
        }
      />
    )

  return (
    <div
      style={styles.container}
      data-testid={
        testId ? `${testId}-risk-assessment-screen` : 'risk-assessment-screen'
      }
    >
      {renderHeader()}
      {renderCreatedBy()}
      {renderFields()}
      {renderOutcomes()}
      {renderActions()}
      {renderSeenBy()}
      {renderFooter()}
    </div>
  )
}

const styles = {
  container: {
    margin: '0 auto',
    maxWidth: sizes.childScreen,
    paddingTop: 24,
    marginBottom: 100,
  },
  fieldContainer: {},
  historyButton: {
    marginRight: 10,
  },
  lastUpdateBox: {
    marginTop: 24,
  },
  sectionContainer: {
    paddingBottom: 40,
  },
  titleContainer: {
    paddingTop: 21,
    display: 'flex',
    background: colors.white,
    padding: '18px 22px',
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 12,
  },
  containerButton: {
    display: 'flex',
  },
  cardContainer: {
    background: colors.white,
    padding: '18px 24px',
    whiteSpace: 'pre-wrap',
    width: '100%',
    fontWeight: 500,
    color: colors.black,
    fontSize: 16,
  },
}

const RISK_ASSESSMENT_MODAL_ID = 'createRiskAssessment'
const FORM_ID = 'editCarePlan'

ResidentRiskAssessmentScreen.propTypes = {
  carers: PropTypes.array,
  resident: PropTypes.object,
  openRiskAssessmentModal: PropTypes.func.isRequired,
  hasAccess: PropTypes.func.isRequired,
  carePlanNominative: PropTypes.string,
  router: PropTypes.object.isRequired,
  loadListItem: PropTypes.func.isRequired,
  testId: PropTypes.string,
}

const mapStateToProps = (state, props) => {
  const {
    router: {
      params: { id },
    },
  } = props

  return {
    carers: state.data.carers,
    resident: find(state.data.residents, { id }),
    carePlanNominative: state.global.home.preferredCarePlansNominative,
    hasAccess: (roles, abilities) =>
      Selectors.checkRoleAccess(state, { roles, abilities }),
  }
}

const mapDispatchToProps = (dispatch) => ({
  openRiskAssessmentModal: (completedRiskAssessment) => {
    dispatch(
      Actions.openModal({
        modalId: RISK_ASSESSMENT_MODAL_ID,
        data: { completedRiskAssessment, parentFormId: FORM_ID },
      }),
    )
  },
  loadListItem: (list_id, item_id) =>
    dispatch(Operations.loadListItem(list_id, item_id)),
})

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ResidentRiskAssessmentScreen),
)
